import { Injectable } from '@angular/core';
import { ActionType } from '../enums/common';
import { DriverDocument } from '../interfaces/driver-firestore';
import { collection, doc, DocumentData, getCountFromServer, getDoc, getDocs, getFirestore, limit, onSnapshot, query, Query, QueryConstraint, QuerySnapshot, updateDoc, where } from 'firebase/firestore';
import { BehaviorSubject, defer, map, Observable, shareReplay } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FireStoreService {
  private readonly firestore = getFirestore();
  constructor() {}
  private driversOnce$?: Observable<DriverDocument[]>;
  private readonly totalRidesSubject = new BehaviorSubject<number>(0);
  totalRides$ = this.totalRidesSubject.asObservable();

  setTotalRidesCount(newCount: number) {
    this.totalRidesSubject.next(newCount);
  }

  async getTotalRidesCount(collectionName: string, filters: QueryConstraint[]) {
    const colRef = collection(this.firestore, collectionName);
    const queryRef = query(colRef, ...filters);
    const countSnap = await getCountFromServer(queryRef);
    this.setTotalRidesCount(countSnap.data().count);
  }

  getPartnersLiveLocations(collectionName: string): Observable<DriverDocument[]> {
    this.driversOnce$ ??= defer(() => {
      const q = query(collection(this.firestore, collectionName), where('partner_id', '!=', null));

      return getDocs(q);
    }).pipe(
      map(snap => snap.docs.map(d => d.data() as DriverDocument)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.driversOnce$;
  }

  getRidesData(collectionName: string, pageSize: number, filters: QueryConstraint[]) {
    console.log(pageSize);
    const colRef = collection(this.firestore, collectionName);
    let q: Query = query(colRef, limit(pageSize), ...filters);

    return new Observable(observer => {
      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot) => {
          let newItems: DocumentData[] = [];
          const changes = snapshot.docChanges().map(change => {
            if (change.type == ActionType.added) newItems.push(change.doc.data());
            return {
              id: change.doc.id,
              data: change.doc.data(),
              type: change.type, // 'added', 'modified', or 'removed',
            };
          });
          observer.next({ newItems, changes });
        },
        error => {
          observer.error(error);
        },
      );

      // Cleanup function to unsubscribe from Firestore updates
      return () => unsubscribe();
    });
  }

  listenToCollectionChanges(collectionName: string, queryConstraints: QueryConstraint[] = []): Observable<any> {
    const colRef = collection(this.firestore, collectionName);
    let q: Query = query(colRef, ...queryConstraints);

    return new Observable(observer => {
      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot) => {
          const changes = snapshot.docChanges().map(change => ({
            id: change.doc.id,
            data: change.doc.data(),
            type: change.type, // 'added', 'modified', or 'removed',
          }));
          observer.next({ changes });
        },
        error => {
          observer.error(error);
        },
      );

      // Cleanup function to unsubscribe from Firestore updates
      return () => unsubscribe();
    });
  }

  listenToDocChanges(docPath: string): Observable<any> {
    const docRef = doc(this.firestore, docPath);

    return new Observable(observer => {
      const unsubscribe = onSnapshot(
        docRef, // Getting the error here
        snapshot => {
          if (snapshot.exists()) {
            observer.next({
              id: snapshot.id,
              data: snapshot.data(),
            });
          }
        },
        error => {
          observer.error(error);
        },
      );

      // Cleanup function to unsubscribe from Firestore updates
      return () => unsubscribe();
    });
  }

  async getDataFromCollection(collectionName: string, queryConstraints: QueryConstraint[] = []) {
    const colRef = collection(this.firestore, collectionName);
    let q: Query = query(colRef, ...queryConstraints);

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      data: doc.data(),
    }));
  }

  async getDocument(docPath: string) {
    const docRef = doc(this.firestore, docPath);
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        data: snapshot.data(),
      };
    }
    
    return null;
  }

  async updateDocField(collectionName: string, docId: string, data: Record<string, any>) {
    const docRef = doc(this.firestore, collectionName, docId);

    // updateDoc only updates specific fields
    await updateDoc(docRef, data);
    return { success: true };
  }
}
